// ============================================================================
// FSA DEPRECIATION — DISPOSAL MODAL  (ADR-0006)
// ============================================================================
// Add/Edit one asset disposal in the Depreciation tab's in-memory draft.
// NBV (= cost − accumulated depreciation) and profit/(loss) on disposal
// (= proceeds − NBV) compute live. IT block suggested from the classification
// (proceeds reduce that block), user-changeable.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import type { BookProrataConvention, DepCalcContext, DepClassConfig } from 'api/types/fsa/depreciationCalc.types';
import {
  depToDisposal,
  effectiveResidualPct,
  estimateAccumulatedDep,
  newLocalId,
  roundTo,
  type DraftDisposal,
} from 'components/modules/fsa/engagement/depreciation/depCalcEngine';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  entry: DraftDisposal | null;
  context: DepCalcContext;
  /** Draft classConfig — supplies sub-class picker options (rev 6) and the carrying-value helper's rate/method (rev 8). */
  classConfig: Record<string, DepClassConfig>;
  roundingPrecision: number;
  /** Rev 8 — first-year pro-rata for the carrying-value helper (and rev 9 in-year dep). */
  bookProrataConvention: BookProrataConvention;
  /** Rev 9 — disposals are charged book dep up to the date of sale. */
  depToDisposalDate: boolean;
  onSave: (entry: DraftDisposal) => void;
  onClose: () => void;
}

interface FormData {
  classCode: string;
  ledgerName: string;
  description: string;
  originalCost: string;
  accumulatedDepreciation: string;
  disposalDate: string;
  disposalProceeds: string;
  itBlockEnumId: string;
}

const INITIAL_FORM: FormData = {
  classCode: '',
  ledgerName: '',
  description: '',
  originalCost: '',
  accumulatedDepreciation: '',
  disposalDate: '',
  disposalProceeds: '',
  itBlockEnumId: '',
};

const FsaDepDisposalModal: React.FC<Props> = ({
  isOpen, mode, entry, context, classConfig, roundingPrecision, bookProrataConvention, depToDisposalDate, onSave, onClose,
}) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Rev 8 — carrying-value helper (not stored: it only fills Accum. Depreciation).
  const [helperOpen, setHelperOpen] = useState(false);
  const [helperPutToUse, setHelperPutToUse] = useState('');
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const initialSnapshotRef = useRef('');

  useEffect(() => {
    if (!isOpen) return;
    const data: FormData = entry
      ? {
          // Picker value = sub-class key when the entry sits on one.
          classCode: entry.subClassLocalId != null ? String(entry.subClassLocalId) : entry.classCode,
          ledgerName: entry.ledgerName ?? '',
          description: entry.description,
          originalCost: String(entry.originalCost || ''),
          accumulatedDepreciation: String(entry.accumulatedDepreciation ?? ''),
          disposalDate: entry.disposalDate,
          disposalProceeds: String(entry.disposalProceeds ?? ''),
          itBlockEnumId: entry.itBlockEnumId != null ? String(entry.itBlockEnumId) : '',
        }
      : { ...INITIAL_FORM };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setHelperOpen(false);
    setHelperPutToUse('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  /** Maps a picker value (class code OR sub-class local id) back to (parent class, sub key). */
  const resolveSelection = useCallback((value: string): { classCode: string; subClassLocalId: string | null } => {
    const cfg = classConfig[value];
    if (cfg?.parent_class_code) return { classCode: cfg.parent_class_code, subClassLocalId: value };
    return { classCode: value, subClassLocalId: null };
  }, [classConfig]);

  const setField = useCallback(<K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'classCode') {
        const parentCode = resolveSelection(value as string).classCode;
        const opt = context.classOptions.find(o => o.classCode === parentCode);
        const suggested = opt?.defaultItBlock
          ? context.itBlockOptions.find(b => b.enumKey === opt.defaultItBlock)
          : undefined;
        next.itBlockEnumId = suggested ? String(suggested.id) : prev.itBlockEnumId;
      }
      return next;
    });
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [context.classOptions, context.itBlockOptions, resolveSelection]);

  // Classes + depreciation-internal sub-classes (rev 6).
  const classOptions = useMemo(() => {
    const opts = context.classOptions.map(o => ({
      value: o.classCode,
      label: `${o.className} (${o.scheduleCode === 'NOTE_INTANGIBLE_ASSETS' ? 'Intangible' : 'PPE'})`,
    }));
    for (const [key, cfg] of Object.entries(classConfig)) {
      if (!cfg.parent_class_code) continue;
      const parent = context.classOptions.find(o => o.classCode === cfg.parent_class_code);
      opts.push({ value: key, label: `${parent?.className ?? cfg.parent_class_code} — ${cfg.name ?? key}` });
    }
    return opts;
  }, [context.classOptions, classConfig]);

  const blockOptions = useMemo(() => context.itBlockOptions.map(o => ({
    value: String(o.id),
    label: o.enumValue,
  })), [context.itBlockOptions]);

  // TB fixed-asset ledgers + the current free-text value (creatable keeps
  // enterprise SyutSelect styling while still allowing any reference).
  const ledgerSelectOptions = useMemo(() => {
    const opts = context.ledgerOptions.map(l => ({ value: l.ledgerName, label: l.ledgerName }));
    if (formData.ledgerName && !opts.some(o => o.value === formData.ledgerName)) {
      opts.unshift({ value: formData.ledgerName, label: formData.ledgerName });
    }
    return opts;
  }, [context.ledgerOptions, formData.ledgerName]);

  const selectedClassOption = context.classOptions.find(
    o => o.classCode === resolveSelection(formData.classCode).classCode);
  const suggestedBlockKey = selectedClassOption?.defaultItBlock ?? null;
  const selectedBlock = context.itBlockOptions.find(b => String(b.id) === formData.itBlockEnumId);
  const blockIsSuggested = suggestedBlockKey != null && selectedBlock?.enumKey === suggestedBlockKey;

  const cost = parseFloat(formData.originalCost) || 0;
  const accum = parseFloat(formData.accumulatedDepreciation) || 0;
  const proceeds = parseFloat(formData.disposalProceeds) || 0;
  // Rev 8 — the selected line's rate + method drive the helper (and rev 9's in-year dep).
  const helperCfg = formData.classCode ? classConfig[formData.classCode] : undefined;
  const helperMethod = helperCfg?.method_effective ?? 'WDV';
  const helperRate = helperCfg?.book_rate ?? 0;
  const helperResidual = helperCfg ? effectiveResidualPct(helperCfg, classConfig) : 0;

  // Rev 9 — same math as the tab's recompute (live preview only).
  const fyStartIso = (context.periodStart ?? '').slice(0, 10);
  const fyEndIso = context.financialYearEnd.slice(0, 10);
  const daysInFy = fyStartIso
    ? Math.max(1, Math.round((Date.parse(`${fyEndIso}T00:00:00Z`) - Date.parse(`${fyStartIso}T00:00:00Z`)) / 86_400_000) + 1)
    : 365;
  const inYearDep = depToDisposalDate && fyStartIso
    ? depToDisposal({
        cost,
        nbvOpen: cost - accum,
        disposalDate: formData.disposalDate,
        method: helperMethod,
        rate: helperRate,
        residualPct: helperMethod === 'SLM' ? helperResidual : 0,
        convention: bookProrataConvention,
        fyStart: fyStartIso,
        daysInFy,
      })
    : 0;
  const nbv = cost - accum - inYearDep;
  const profitLoss = proceeds - nbv;
  const helperEstimate = helperOpen && helperPutToUse
    ? estimateAccumulatedDep({
        cost,
        putToUseDate: helperPutToUse,
        method: helperMethod,
        rate: helperRate,
        residualPct: helperResidual,
        convention: bookProrataConvention,
        fyStart: (context.periodStart ?? '').slice(0, 10),
      })
    : null;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.description.trim()) errors.description = 'Description is required.';
    if (!formData.classCode) errors.classCode = 'Asset classification is required.';
    if (!formData.disposalDate) errors.disposalDate = 'Disposal date is required.';
    if (!formData.originalCost || Number.isNaN(parseFloat(formData.originalCost)) || cost <= 0) {
      errors.originalCost = 'Enter a positive original cost.';
    }
    if (formData.accumulatedDepreciation === '' || Number.isNaN(parseFloat(formData.accumulatedDepreciation)) || accum < 0) {
      errors.accumulatedDepreciation = 'Enter accumulated depreciation (0 or more).';
    }
    if (accum > cost) errors.accumulatedDepreciation = 'Accumulated depreciation cannot exceed the original cost.';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }

    const resolved = resolveSelection(formData.classCode);
    const opt = context.classOptions.find(o => o.classCode === resolved.classCode);
    const saved: DraftDisposal = {
      localId: entry?.localId ?? newLocalId(),
      scheduleId: opt?.scheduleId ?? entry?.scheduleId ?? '',
      scheduleClassId: opt ? opt.scheduleClassId : entry?.scheduleClassId ?? null,
      customClassLocalId: opt ? opt.customClassLocalId : entry?.customClassLocalId ?? null,
      classCode: resolved.classCode,
      subClassLocalId: resolved.subClassLocalId,
      ledgerName: formData.ledgerName.trim() || null,
      description: formData.description.trim(),
      originalCost: cost,
      accumulatedDepreciation: accum,
      disposalDate: formData.disposalDate,
      disposalProceeds: proceeds,
      itBlockEnumId: formData.itBlockEnumId || null,
      displayOrder: entry?.displayOrder ?? 0,
      // Recomputed authoritatively by recomputeDraft at tab level.
      depToDisposalActual: null,
      depToDisposalRounded: null,
      netBookValue: null,
      profitLossOnDisposal: null,
    };
    onSave(saved);
    onClose();
  }, [formData, cost, accum, proceeds, entry, context.classOptions, resolveSelection, onSave, onClose]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  if (!isOpen) return null;

  const fmt = (v: number) => roundTo(v, roundingPrecision)
    .toLocaleString('en-IN', { minimumFractionDigits: roundingPrecision, maximumFractionDigits: roundingPrecision });

  return (
    <>
      <div className="modal-backdrop" onClick={handleClose} />
      <div className="modal-container modal-md" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <IconSystem.trash size={16} />
            <h2 className="modal-title" style={{ margin: 0 }}>
              {mode === 'create' ? 'Add Disposal' : 'Edit Disposal'}
            </h2>
          </div>
          <button className="modal-close" onClick={handleClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ flex: 1, overflow: 'auto' }}>
          <div className="form-group" data-field="description">
            <label className="form-label">Description <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${fieldErrors.description ? ' form-field-error' : ''}`}
              value={formData.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="e.g. Old file server"
              maxLength={300}
            />
            {fieldErrors.description && <div className="form-error-message">{fieldErrors.description}</div>}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="classCode">
              <SyutSelect
                label={<>Asset Classification <span className="text-danger">*</span></>}
                value={formData.classCode}
                onChange={(v) => setField('classCode', (v as string) || '')}
                options={classOptions}
                isSearchable
                error={fieldErrors.classCode}
                placeholder="Select class…"
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label={<>Ledger <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></>}
                value={formData.ledgerName}
                onChange={(v) => setField('ledgerName', (v as string) || '')}
                options={ledgerSelectOptions}
                isCreatable
                onCreateOption={(v) => setField('ledgerName', v)}
                formatCreateLabel={(v) => `Use "${v}"`}
                isSearchable
                isClearable
                placeholder="Pick a TB ledger or type…"
              />
            </div>
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="originalCost">
              <label className="form-label">Original Cost <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${fieldErrors.originalCost ? ' form-field-error' : ''}`}
                value={formData.originalCost}
                onChange={(e) => setField('originalCost', e.target.value)}
                min={0}
                step="0.01"
                style={{ textAlign: 'right' }}
              />
              {fieldErrors.originalCost && <div className="form-error-message">{fieldErrors.originalCost}</div>}
            </div>
            <div className="form-group" data-field="accumulatedDepreciation">
              <label className="form-label">Accum. Depreciation <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${fieldErrors.accumulatedDepreciation ? ' form-field-error' : ''}`}
                value={formData.accumulatedDepreciation}
                onChange={(e) => setField('accumulatedDepreciation', e.target.value)}
                min={0}
                step="0.01"
                style={{ textAlign: 'right' }}
              />
              {fieldErrors.accumulatedDepreciation && <div className="form-error-message">{fieldErrors.accumulatedDepreciation}</div>}
            </div>
            <div className="form-group" data-field="disposalDate">
              <label className="form-label">Disposal Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.disposalDate ? ' form-field-error' : ''}`}
                value={formData.disposalDate}
                onChange={(e) => setField('disposalDate', e.target.value)}
              />
              {fieldErrors.disposalDate && <div className="form-error-message">{fieldErrors.disposalDate}</div>}
            </div>
          </div>

          {/* Rev 8 — carrying-value helper: rolls the asset forward from its
              put-to-use date to the start of this year at the class's rate. */}
          <div style={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
            {!helperOpen ? (
              <button type="button" style={linkBtnStyle} onClick={() => setHelperOpen(true)}>
                Work out accumulated depreciation from the put-to-use date…
              </button>
            ) : (
              <div style={helperBoxStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Put to use on</span>
                  <input
                    type="date"
                    className="form-input"
                    style={{ width: 160, padding: '2px 6px', fontSize: '0.78rem' }}
                    value={helperPutToUse}
                    onChange={(e) => setHelperPutToUse(e.target.value)}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    → {helperEstimate == null ? '—' : fmt(helperEstimate)}
                  </span>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={helperEstimate == null}
                    onClick={() => helperEstimate != null && setField('accumulatedDepreciation', String(roundTo(helperEstimate, roundingPrecision)))}
                  >
                    Use this
                  </button>
                  <button type="button" style={linkBtnStyle} onClick={() => setHelperOpen(false)}>
                    Close
                  </button>
                </div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                  {!formData.classCode || helperRate <= 0
                    ? 'Pick an asset classification with a rate first.'
                    : `${helperMethod} at the class's current rate ${helperRate}%${helperMethod === 'SLM' ? `, residual ${helperResidual}%` : ''}, first year ${bookProrataConvention === 'HALF_RATE_180' ? 'on the 180-day rule' : 'day-wise'}, up to the start of this year${depToDisposalDate ? ' (this year\'s depreciation to the sale date is added separately)' : ''}. An estimate — if earlier years used other rates, enter the books' figure instead.`}
                </div>
              </div>
            )}
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Disposal Proceeds</label>
              <input
                type="number"
                className="form-input"
                value={formData.disposalProceeds}
                onChange={(e) => setField('disposalProceeds', e.target.value)}
                min={0}
                step="0.01"
                placeholder="0.00"
                style={{ textAlign: 'right' }}
              />
            </div>
            <div className="form-group" data-field="itBlockEnumId">
              <SyutSelect
                label={
                  <>
                    Income Tax Block
                    {blockIsSuggested && (
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                        suggested from class
                      </span>
                    )}
                  </>
                }
                value={formData.itBlockEnumId}
                onChange={(v) => setField('itBlockEnumId', (v as string) || '')}
                options={blockOptions}
                isSearchable
                placeholder="Block reduced by proceeds…"
              />
            </div>
          </div>

          {/* Live computed strip */}
          {cost > 0 && (
            <div style={previewStripStyle}>
              {depToDisposalDate && <PreviewItem label="Dep. to sale date" value={fmt(inYearDep)} />}
              <PreviewItem label={depToDisposalDate ? 'WDV at sale' : 'Net book value'} value={fmt(nbv)} />
              <PreviewItem
                label="Profit / (loss)"
                value={profitLoss < 0 ? `(${fmt(Math.abs(profitLoss))})` : fmt(profitLoss)}
                tone={profitLoss < 0 ? 'loss' : 'profit'}
              />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}>
            {mode === 'create' ? 'Add Disposal' : 'Apply Changes'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes on this disposal. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const PreviewItem: React.FC<{ label: string; value: string; tone?: 'profit' | 'loss' }> = ({ label, value, tone }) => (
  <div>
    <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)' }}>{label}</div>
    <div style={{
      fontSize: '0.85rem', fontWeight: 600, marginTop: 2, fontVariantNumeric: 'tabular-nums',
      color: tone === 'loss' ? '#dc2626' : tone === 'profit' ? '#16a34a' : 'var(--text-primary)',
    }}>
      {value}
    </div>
  </div>
);

const linkBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  fontSize: '0.75rem',
  color: 'var(--primary-color, #2563eb)',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const helperBoxStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 6,
  border: '1px dashed var(--border-color)',
  background: 'var(--bg-secondary)',
};

const previewStripStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '0.75rem',
  padding: '0.65rem 0.85rem',
  marginTop: '0.25rem',
  borderRadius: 6,
  border: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};

export default FsaDepDisposalModal;
